import { createTheme } from '@mui/material/styles';
import type { ThemeOptions } from '@mui/material/styles';

import { BRAND_COLORS } from '@/constants/branding';

const sharedTypography: ThemeOptions['typography'] = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  body1: { fontSize: '0.875rem' },
  body2: { fontSize: '0.8125rem' },
};

const sharedComponents: ThemeOptions['components'] = {
  MuiButton: {
    defaultProps: { variant: 'contained', disableElevation: true },
  },
  MuiPaper: {
    defaultProps: { elevation: 1 },
  },
  MuiTextField: {
    defaultProps: { size: 'small', variant: 'outlined' },
  },
  MuiSkeleton: {
    defaultProps: { variant: 'rectangular' },
  },
  MuiAlert: {
    defaultProps: { variant: 'filled' },
  },
  MuiIconButton: {
    defaultProps: { size: 'small' },
  },
  MuiLinearProgress: {
    defaultProps: { variant: 'determinate' },
  },
  MuiSelect: {
    defaultProps: { size: 'small' },
  },
  MuiToggleButtonGroup: {
    defaultProps: { size: 'small' },
  },
  // Tooltips carry one-sentence explanations, not just button names - keep them readable.
  // describeChild: the tooltip text is announced as a description, so a button or field keeps its
  // visible label (or aria-label) as its accessible name instead of the whole sentence (WCAG 2.5.3)
  MuiTooltip: {
    defaultProps: { describeChild: true },
    styleOverrides: {
      tooltip: { fontSize: '0.75rem', lineHeight: 1.5, maxWidth: 320 },
    },
  },
};

export const lightTheme = createTheme({
  cssVariables: true,
  palette: {
    mode: 'light',
    primary: { main: BRAND_COLORS.NAVY, light: BRAND_COLORS.LIGHT_NAVY, contrastText: '#ffffff' },
    secondary: { main: BRAND_COLORS.TEAL },
    success: { main: '#2e7d32' },
    warning: { main: '#ed6c02' },
    error: { main: '#d32f2f' },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: sharedTypography,
  spacing: 8,
  components: sharedComponents,
});

export const darkTheme = createTheme({
  cssVariables: true,
  palette: {
    mode: 'dark',
    primary: { main: BRAND_COLORS.TEAL, contrastText: '#ffffff' },
    secondary: { main: BRAND_COLORS.GREEN },
    success: { main: '#66bb6a' },
    warning: { main: '#ffa726' },
    error: { main: '#f44336' },
    background: {
      default: '#0e1726',
      paper: BRAND_COLORS.NAVY,
    },
  },
  typography: sharedTypography,
  spacing: 8,
  components: sharedComponents,
});
